function toolInvocationHasResult(t) {
    return 'result' in t && t.result != null;
}
function extractToolCallId(t) {
    const inv = t;
    const tid = inv.toolCallId ?? inv.id;
    return typeof tid === 'string' && tid.length > 0 ? tid : undefined;
}
/** Same id resolution as message `toolInvocations` — prefer `toolCallId` over `id`. */
export function extractSelectionWizardToolCallId(toolCall) {
    const tid = toolCall.toolCallId ?? toolCall.id;
    return typeof tid === 'string' && tid.length > 0 ? tid : `wizard-${Date.now()}`;
}
function extractWizardIdFromInvocation(t) {
    const args = t.args;
    return typeof args?.wizardId === 'string' && args.wizardId.length > 0
        ? args.wizardId
        : undefined;
}
function normalizeStoredAnswers(raw) {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw))
        return {};
    const out = {};
    Object.entries(raw).forEach(([k, v]) => {
        if (typeof v === 'string')
            out[k] = v;
        else if (Array.isArray(v) && v.every((x) => typeof x === 'string'))
            out[k] = v;
    });
    return out;
}
/** Latest assistant selectionWizard tool result with wizardId and saved answers (for edit prefill). */
export function getLatestCompletedSelectionWizardState(messages) {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
        const m = messages[i];
        if (m.role !== 'assistant' || !m.toolInvocations?.length)
            continue;
        for (let j = m.toolInvocations.length - 1; j >= 0; j -= 1) {
            const t = m.toolInvocations[j];
            if (t.toolName !== 'selectionWizard')
                continue;
            if (!toolInvocationHasResult(t))
                continue;
            const raw = t.result;
            if (!raw || typeof raw !== 'object')
                continue;
            const wizardId = raw.wizardId;
            if (typeof wizardId !== 'string' || wizardId.length === 0)
                continue;
            return {
                wizardId,
                answers: normalizeStoredAnswers(raw.answers),
            };
        }
    }
    return null;
}
/**
 * After reload from localStorage there is no live `pendingInlineWizard` promise; find a pending
 * selectionWizard tool on the most recent assistant message (exactly one pending call).
 */
export function getResumableSelectionWizardPlacement(messages, wizardsWithStepsLength, isToolCallResolved) {
    if (wizardsWithStepsLength === 0)
        return null;
    for (let i = messages.length - 1; i >= 0; i -= 1) {
        const m = messages[i];
        if (m.role !== 'assistant' || !m.toolInvocations?.length)
            continue;
        const pending = m.toolInvocations.filter((t) => t.toolName === 'selectionWizard' && !toolInvocationHasResult(t));
        if (pending.length !== 1)
            continue;
        const t = pending[0];
        const toolCallId = extractToolCallId(t);
        if (!toolCallId)
            continue;
        if (isToolCallResolved?.(toolCallId))
            continue;
        return { toolCallId, wizardId: extractWizardIdFromInvocation(t) };
    }
    return null;
}
/**
 * Whether the **selection wizard should take over the main conversation column** (full-height wizard)
 * instead of the normal **conversation mode** (message list + welcome empty state).
 *
 * **Conversation mode** (returns `false`):
 * - Main column: `StreamingMessages` or welcome UI.
 * - `SelectionWizardComposerArea` lives in the **footer** strip: shows `ChatInput`, or the wizard UI in
 *   that strip when a tool call is active (legacy narrow layout).
 *
 * **Wizard main-column mode** (returns `true`):
 * - Main column: only `SelectionWizardComposerArea` with `fillsConversationArea` — wizard is top-aligned
 *   and uses the full message area; message list is hidden.
 * - Footer: input carousel only when needed, not a second copy of the wizard.
 *
 * Truth table: portal must define at least one wizard with steps, and either
 * - user chose **Edit** (`pendingComposerEdit`), or
 * - `getSelectionWizardComposerPlacement` finds a binding (live `pendingInlineWizard`, or resumable pending
 *   `selectionWizard` tool on the last assistant message after reload).
 *
 * **`messages` must reflect the live `useChat` thread** (ideally after `normalizeMessagesForAddToolResult` so
 * `toolInvocations` matches the AI SDK). Do not pass only MobX — it lags behind `useChat` after tools complete.
 */
export function shouldSelectionWizardOccupyMainColumn(messages, pendingInlineWizard, 
/** From `useSelectionWizard().pendingComposerEdit` — edit flow reopens wizard in the main column. */
pendingComposerEdit, wizardsWithStepsLength, isToolCallResolved) {
    if (wizardsWithStepsLength === 0)
        return false;
    if (pendingComposerEdit != null)
        return true;
    return getSelectionWizardComposerPlacement(messages, pendingInlineWizard, wizardsWithStepsLength, isToolCallResolved) != null;
}
/**
 * Resolves which toolCallId (and wizardId when known) to bind the composer wizard to.
 * Works for a live tool call (`pendingInlineWizard` from useChat) or after persistence reload
 * (`getResumableSelectionWizardPlacement`).
 */
export function getSelectionWizardComposerPlacement(messages, pendingInlineWizard, wizardsWithStepsLength, isToolCallResolved) {
    if (wizardsWithStepsLength === 0) {
        return null;
    }
    // Live tool call governs while its promise is pending and the user has not finished it.
    if (pendingInlineWizard) {
        const p = pendingInlineWizard;
        if (isToolCallResolved?.(p.toolCallId)) {
            return null;
        }
        return { toolCallId: p.toolCallId, wizardId: p.wizardId };
    }
    // No live promise (e.g. after reload): reopen a single unresolved pending tool call.
    return getResumableSelectionWizardPlacement(messages, wizardsWithStepsLength, isToolCallResolved);
}
/** wizardId only — convenience over getLatestCompletedSelectionWizardState. */
export function getLatestCompletedSelectionWizardWizardId(messages) {
    return getLatestCompletedSelectionWizardState(messages)?.wizardId ?? null;
}
//# sourceMappingURL=selectionWizardComposerUtils.js.map